import {AxiosResponse} from 'axios';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {useRecoilValue} from 'recoil';
import {RawTariffDto} from '../../../global/api';
import {usePostBillingTariffsMutation} from '../../../global/services/billing';
import {usePostCompaniesFindByIdsQuerySuspense} from '../../../global/services/companies';
import {useRefreshCompanyTariff} from '../../company-tariff';
import {checkAuthorities} from '../../../shared/helpers/checkAuthorities';
import {ToastHelpers} from '../../../shared/helpers/toastHelpers';
import {authState} from '../../../store/auth/atoms';
import {TARIFF_CONNECT_ALLOWED_ROLES} from '../constants/roles.constants';
import {useTariffs} from './use-tariffs.hook';
import {PeriodKey} from '../types/client-tariffs.types';
import {
  getCanonicalPeriodCode,
  getDurationByPeriod,
  getPeriodCodeByKey,
  getPeriodDescription,
  getSaveHint,
  normalizeTariffTitle,
} from '../utils/formatters';

type ConnectPlanSuccessHandler = (response: AxiosResponse<unknown>) => Promise<void> | void;

export const useClientTariffsModel = () => {
  const {user} = useRecoilValue(authState);
  const companyId = user?.companyId ? [user.companyId] : [];
  const {data, refresh: refreshCompany} = usePostCompaniesFindByIdsQuerySuspense(companyId);
  const refreshCompanyTariff = useRefreshCompanyTariff();
  const {plans, refreshTariffs} = useTariffs();
  const {mutate: connectTariff, loading: connectingTariff} = usePostBillingTariffsMutation();

  const [period, setPeriod] = useState<PeriodKey>('year');
  const [selectedPlanTitle, setSelectedPlanTitle] = useState<string | null>(null);

  const currentPlanTitleRaw = data?.[0]?.tariff?.title ?? 'СТАРТ';
  const currentPlanTitleNormalized = normalizeTariffTitle(data?.[0]?.tariff?.title);
  const currentPlanPeriod = getCanonicalPeriodCode(data?.[0]?.tariff?.period);
  const selectedPeriodCode = getPeriodCodeByKey(period);

  const periodDescription = useMemo(() => {
    if (currentPlanPeriod === '6M') return getPeriodDescription('halfYear');
    if (currentPlanPeriod === '1Y') return getPeriodDescription('year');
    return getPeriodDescription('month');
  }, [currentPlanPeriod]);

  useEffect(() => {
    const existsInPlans = plans.some((plan) => plan.title === selectedPlanTitle);
    if (selectedPlanTitle && existsInPlans) return;

    const currentPlanFromList = plans.find((plan) => normalizeTariffTitle(plan.title) === currentPlanTitleNormalized);
    setSelectedPlanTitle(currentPlanFromList?.title ?? plans[0]?.title ?? null);
  }, [plans, selectedPlanTitle, currentPlanTitleNormalized]);

  const selectedPlanDurations = useMemo(
    () => plans.find((plan) => plan.title === selectedPlanTitle)?.durations ?? [],
    [plans, selectedPlanTitle],
  );

  const saveHint = useMemo(() => getSaveHint(period, selectedPlanDurations), [period, selectedPlanDurations]);
  const showSaveHint = selectedPeriodCode !== '1M' && Boolean(saveHint);

  const connectPlan = useCallback(
    (plan: RawTariffDto, onSuccess?: ConnectPlanSuccessHandler) => {
      const isCurrentPlan =
        normalizeTariffTitle(plan.title) === currentPlanTitleNormalized && selectedPeriodCode === currentPlanPeriod;
      if (isCurrentPlan) return;

      const canConnectTariff = checkAuthorities(TARIFF_CONNECT_ALLOWED_ROLES, user?.roles || []);
      if (!canConnectTariff) {
        ToastHelpers.error('Недостаточно прав для подключения тарифа');
        return;
      }
      if (!user?.companyId) {
        ToastHelpers.error('Не найден companyId');
        return;
      }
      if (!plan.id) {
        ToastHelpers.error('Не найден id тарифа');
        return;
      }
      const selectedDuration = getDurationByPeriod(period, plan.durations);
      if (!selectedDuration?.id) {
        ToastHelpers.error('Не найден id периода тарифа');
        return;
      }

      connectTariff(
        {
          companyId: user.companyId,
          tariffId: plan.id,
          durationId: selectedDuration.id,
        },
        async (response) => {
          refreshTariffs();
          refreshCompany();
          refreshCompanyTariff();
          await onSuccess?.(response);
        },
        {skipSuccessToast: true},
      );
    },
    [
      connectTariff,
      currentPlanPeriod,
      currentPlanTitleNormalized,
      period,
      refreshCompany,
      refreshCompanyTariff,
      refreshTariffs,
      selectedPeriodCode,
      user,
    ],
  );

  return {
    plans,
    period,
    setPeriod,
    selectedPlanTitle,
    setSelectedPlanTitle,
    selectedPeriodCode,
    connectingTariff,
    currentPlanTitleRaw,
    currentPlanTitleNormalized,
    currentPlanPeriod,
    periodDescription,
    saveHint,
    showSaveHint,
    connectPlan,
  };
};
